import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Camera, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useApp } from "@/contexts/AppContext";

const SCAN_STAGES = [
  { en: "Uploading image...", id: "Mengunggah gambar..." },
  { en: "Reading text from receipt...", id: "Membaca teks dari struk..." },
  { en: "Identifying items & prices...", id: "Mengenali item & harga..." },
  { en: "Calculating totals...", id: "Menghitung total..." },
];

const useScanStage = (active) => {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    if (!active) { setStage(0); return; }
    const timers = [3000, 7000, 12000].map((ms, i) =>
      setTimeout(() => setStage(i + 1), ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [active]);
  return stage;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BillScannerModal = ({ group, onBillScanned, onClose }) => {
  const { t, language } = useApp();
  const [step, setStep] = useState('capture'); // capture | scanning | results
  const [error, setError] = useState('');
  const [scannedData, setScannedData] = useState(null);
  const fileRef = useRef(null);
  const scanStage = useScanStage(step === 'scanning');

  const processImage = async (file) => {
    if (!file) return;
    setStep('scanning');
    setError('');

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result.split(',')[1];
      try {
        const res = await axios.post(`${API}/scan-bill`, {
          image_base64: base64,
          currency: group.currency
        });
        setScannedData(res.data);
        setStep('results');
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (detail === 'INSUFFICIENT_BALANCE') {
          setError(t('scan_error_balance'));
        } else {
          setError(t('scan_error'));
        }
        setStep('capture');
      }
    };
    reader.onerror = () => { setError(t('scan_error')); setStep('capture'); };
    reader.readAsDataURL(file);
  };

  const openCamera = () => {
    fileRef.current.setAttribute('capture', 'camera');
    fileRef.current.click();
  };

  const openFile = () => {
    fileRef.current.removeAttribute('capture');
    fileRef.current.click();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="scan-bill-modal">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl">{t('scan_bill')}</DialogTitle>
        </DialogHeader>

        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={e => e.target.files[0] && processImage(e.target.files[0])}
          className="hidden"
          data-testid="camera-upload-input"
        />

        {step === 'capture' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-zinc-200 rounded-2xl p-8 text-center">
              <Camera size={40} className="mx-auto text-zinc-300 mb-3" strokeWidth={1.5} />
              <p className="text-sm text-zinc-500 mb-5">{t('scan_bill_desc')}</p>
              <div className="flex flex-col gap-3">
                <Button onClick={openCamera} className="bg-zinc-900 hover:bg-zinc-800 text-white h-11" data-testid="take-photo-btn">
                  <Camera size={15} strokeWidth={1.5} className="mr-2" /> {t('take_photo')}
                </Button>
                <Button variant="outline" onClick={openFile} className="border-zinc-200 h-11" data-testid="upload-file-btn">
                  <Upload size={15} strokeWidth={1.5} className="mr-2" /> {t('upload_file')}
                </Button>
              </div>
              {error && <p className="text-rose-600 text-sm mt-4">{error}</p>}
            </div>
          </div>
        )}

        {step === 'scanning' && (
          <div className="py-14 text-center">
            <div className="relative w-14 h-14 mx-auto mb-5">
              <div className="absolute inset-0 border-4 border-zinc-200 rounded-full" />
              <div className="absolute inset-0 border-4 border-transparent border-t-zinc-900 rounded-full animate-spin" />
            </div>
            <p className="font-medium text-zinc-900 transition-all duration-300">
              {SCAN_STAGES[scanStage][language]}
            </p>
            <div className="flex justify-center gap-1.5 mt-4">
              {SCAN_STAGES.map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full transition-colors duration-300 ${i <= scanStage ? 'bg-zinc-900' : 'bg-zinc-200'}`} />
              ))}
            </div>
          </div>
        )}

        {step === 'results' && scannedData && (
          <div className="space-y-4">
            <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4">
              <h3 className="font-semibold text-zinc-900 mb-3">{scannedData.title || t('scan_bill')}</h3>
              <div className="space-y-1.5">
                {scannedData.items?.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-zinc-600 flex items-center gap-1.5">
                      {it.is_tax && (
                        <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                          {t('tax')}
                        </span>
                      )}
                      {it.name}
                    </span>
                    <span className="text-zinc-900 font-medium">{Number(it.amount || 0).toLocaleString('id-ID')}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-zinc-200 flex justify-between">
                <span className="font-semibold text-zinc-900">{t('total')}</span>
                <span className="font-bold text-zinc-900">{Number(scannedData.total || 0).toLocaleString('id-ID')}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep('capture')} className="flex-1 border-zinc-200">
                {t('retake')}
              </Button>
              <Button
                onClick={() => onBillScanned(scannedData)}
                className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white"
                data-testid="scan-confirm-btn"
              >
                {t('use_this_bill')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BillScannerModal;

